import React, { useState, useEffect } from 'react';

// ── TypeScript Interfaces ────────────────────────────────────────────────────

interface WedgeItem {
  wedge_id: number;
  manufacturer: string;
  model: string;
  loft: number;
  grind: string;
  bounce: number;
  year: number;
  wedge_head_score: number;           // 0–10 scale (already correct from backend)
  brand_score: number;
  recency_score: number;
  grind_bounce_score: number;
  loft_score: number;
  recommended_grind: string;
  recommended_bounce: number;
  grind_reasoning: string;
  bounce_reasoning: string;
  explanation?: string;
}

interface WedgeShaftRecommendation {
  shaft_name: string;
  shaft_flex: string;
  material: string;
  weight_approx: string;
  score?: number;
  reason: string;
  note?: string;
}

interface WedgeMetadata {
  scoring_mode: string;
  scoring_version: string;
  total_wedges_evaluated: number;
  per_type_shown: number;
  ideal_lofts: { gap: number; sand: number; lob: number };
  existing_wedge_lofts: number[] | null;
  loft_note?: string;
}

interface WedgeRecommendationsData {
  wedge_types: { gap: WedgeItem[]; sand: WedgeItem[]; lob: WedgeItem[] };
  shaft_recommendation: WedgeShaftRecommendation;
  metadata: WedgeMetadata;
  status: string;
  equipment_type: string;
}

interface WedgeRecommendationResultsProps {
  data: WedgeRecommendationsData | any | null;
  loading?: boolean;
  error?: string | null;
  onBack?: () => void;
  className?: string;
}

// ── Category display config ──────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  gap: 'Gap Wedges',
  sand: 'Sand Wedges',
  lob: 'Lob Wedges',
};

// ── Main Component ───────────────────────────────────────────────────────────

const WedgeRecommendationResults: React.FC<WedgeRecommendationResultsProps> = ({
  data,
  loading = false,
  error = null,
  onBack,
  className = '',
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [shaftNoteCollapsed, setShaftNoteCollapsed] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Expand card #1 of the first non-empty category on mount
  useEffect(() => {
    if (data && !initialized) {
      const first = ['gap', 'sand', 'lob'].find(
        t => data.wedge_types?.[t]?.length > 0
      );
      if (first) {
        const firstId = data.wedge_types[first][0]?.wedge_id;
        if (firstId !== undefined) setExpandedCards(new Set([firstId]));
      }
      setInitialized(true);
    }
  }, [data, initialized]);

  const toggleCard = (wedgeId: number) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(wedgeId)) next.delete(wedgeId);
      else next.add(wedgeId);
      return next;
    });
  };

  // ── Explanation helpers ──────────────────────────────────────────────────

  const BOLD_TERMS_REGEX = new RegExp(
    [
      '\\b\\d+\\s*mph\\b',
      '\\b\\d+g\\b',
      '\\d+(?:\\.\\d+)?°',
      '\\b\\d[,\\d]*\\s*rpm\\b',
      '\\bvery high MOI\\b',
      '\\bhigh MOI\\b',
      '\\bextremely forgiving\\b',
      '\\bvery forgiving\\b',
      '\\bstrong forgiveness\\b',
      '\\bforgiving\\b',
      '\\bextra stiff\\b',
      '\\bTour X\\b',
      '\\bstiff\\b',
      '\\bregular flex\\b',
      '\\bsenior flex\\b',
      '\\bladies flex\\b',
      '\\blow-spin\\b',
      '\\bworkable\\b',
      '\\boptimal\\b',
      '\\bM-Grind\\b',
      '\\bD-Grind\\b',
      '\\bK-Grind\\b',
      '\\bS-Grind\\b',
      '\\bsteep attack\\b',
      '\\bshallow\\b',
      '\\bneutral attack\\b',
      '\\bhigh bounce\\b',
      '\\blow bounce\\b',
      'tour.?standard',
      '\\bbunker\\b',
    ].join('|'),
    'gi'
  );

  const highlightText = (text: string): React.ReactNode => {
    const captureRegex = new RegExp(`(${BOLD_TERMS_REGEX.source})`, 'gi');
    const parts = text.split(captureRegex);
    return (
      <>
        {parts.map((part, i) =>
          i % 2 === 1 && part ? (
            <strong key={i} className="font-semibold text-gray-800">
              {part}
            </strong>
          ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
          )
        )}
      </>
    );
  };

  const extractKeywordTags = (explanation: string): string[] => {
    const tags: string[] = [];
    const lower = explanation.toLowerCase();
    if (/tour.?proven|tour standard/i.test(lower)) tags.push('Tour Proven');
    if (/forgiving|forgiveness/i.test(lower)) tags.push('Forgiving');
    if (/versatil/i.test(lower)) tags.push('Versatile');
    if (/steep attack/i.test(lower)) tags.push('Steep Attack');
    if (/shallow/i.test(lower)) tags.push('Shallow Swing');
    if (/neutral attack|neutral swing/i.test(lower)) tags.push('Neutral Swing');
    if (/high.?spin|spin/i.test(lower)) tags.push('High Spin');
    if (/bunker/i.test(lower)) tags.push('Bunker Ready');
    return tags.slice(0, 4);
  };

  const matchesExistingLoft = (loft: number): boolean => {
    const existing = data?.metadata?.existing_wedge_lofts;
    if (!existing || existing.length === 0) return false;
    return existing.some((el: number) => Math.abs(el - loft) <= 0.5);
  };

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700">
            Analyzing Wedge Options
          </h3>
          <p className="text-gray-500 mt-2">
            Finding your perfect wedge matches...
          </p>
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Wedge Recommendations
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              {onBack && (
                <div className="mt-4">
                  <button
                    onClick={onBack}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 font-medium text-sm"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── No data state ────────────────────────────────────────────────────────
  if (!data || !data.wedge_types) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-yellow-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                No Wedge Recommendations Found
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  We couldn't find any wedge recommendations. Please try
                  adjusting your criteria.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Per-wedge card renderer ──────────────────────────────────────────────
  const renderWedgeCard = (wedge: WedgeItem, rank: number) => {
    const isExpanded = expandedCards.has(wedge.wedge_id);
    const explanation = wedge.explanation || '';
    const keywordTags = extractKeywordTags(explanation);
    const scoreWidth = Math.min((wedge.wedge_head_score / 10) * 100, 100);
    const hasGrindBounce =
      wedge.recommended_grind || wedge.recommended_bounce !== undefined;

    return (
      <div
        key={wedge.wedge_id}
        className="bg-white rounded-lg shadow-sm border border-gray-200 transition-all"
      >
        {/* Card Header — click to expand/collapse */}
        <button
          className="w-full text-left px-6 py-4 border-b border-gray-100"
          onClick={() => toggleCard(wedge.wedge_id)}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-3">
            {/* Rank badge */}
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-ink text-white font-bold text-sm shrink-0">
              {rank}
            </span>

            {/* Title block */}
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-gray-900 leading-tight">
                {wedge.manufacturer} {wedge.model}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                {wedge.loft}° &bull; Grind: {wedge.grind} &bull; Bounce:{' '}
                {wedge.bounce}° &bull; {wedge.year}
              </p>
            </div>

            {/* Score bar + label */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-20 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-ink h-2 rounded-full"
                  style={{ width: `${scoreWidth}%` }}
                />
              </div>
              <span className="text-sm font-bold text-gray-700 w-10 text-right">
                {wedge.wedge_head_score.toFixed(1)}/10
              </span>

              {/* Expand chevron */}
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs shrink-0 transition-colors ${
                  isExpanded
                    ? 'bg-green/15 text-green-ink'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <span className="hidden sm:inline text-[10px] font-medium">
                  {isExpanded ? 'Less' : 'Details'}
                </span>
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </div>
          </div>

          {/* Collapsed teaser: keyword tags */}
          {!isExpanded && (
            <div className="mt-2 ml-11 flex items-center gap-1.5 flex-wrap">
              {keywordTags.length > 0 ? (
                keywordTags.map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green/10 text-green-ink border border-green-ink"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-400">Show details</span>
              )}
            </div>
          )}
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-6 py-4 space-y-4">
            {/* 1. Explanation */}
            {explanation && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {highlightText(explanation)}
              </p>
            )}

            {/* 2. Grind & Bounce Recommendation box */}
            {hasGrindBounce && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-amber-600">⚡</span>
                  <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                    Recommended for Your Swing
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {wedge.recommended_grind && (
                    <span className="inline-flex items-center bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2.5 py-1 text-xs font-bold">
                      {wedge.recommended_grind} Grind
                    </span>
                  )}
                  {wedge.recommended_bounce !== undefined && (
                    <span className="inline-flex items-center bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2.5 py-1 text-xs font-bold">
                      {wedge.recommended_bounce}° Bounce
                    </span>
                  )}
                </div>
                {wedge.grind_reasoning && (
                  <p className="text-xs text-amber-700 leading-snug">
                    {wedge.grind_reasoning}
                  </p>
                )}
                {wedge.bounce_reasoning && (
                  <p className="text-xs text-amber-600 leading-snug mt-1">
                    {wedge.bounce_reasoning}
                  </p>
                )}
              </div>
            )}

            {/* 3. Score Breakdown */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-xs text-gray-500">
                Brand:{' '}
                <span className="font-medium text-gray-700">
                  {wedge.brand_score?.toFixed(1) ?? '—'}
                </span>
              </span>
              <span className="text-xs text-gray-500">
                Year:{' '}
                <span className="font-medium text-gray-700">
                  {wedge.recency_score?.toFixed(1) ?? '—'}
                </span>
              </span>
              <span className="text-xs text-gray-500">
                Grind/Bounce Fit:{' '}
                <span className="font-medium text-gray-700">
                  {wedge.grind_bounce_score?.toFixed(1) ?? '—'}
                </span>
              </span>
              <span className="text-xs text-gray-500">
                Loft:{' '}
                <span className="font-medium text-gray-700">
                  {wedge.loft_score?.toFixed(1) ?? '—'}
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────
  const idealLofts = data.metadata?.ideal_lofts;
  const existingLofts = data.metadata?.existing_wedge_lofts;
  const hasExistingLofts = existingLofts && existingLofts.length > 0;
  const shaft = data.shaft_recommendation;

  // Count total wedges shown
  const totalShown = ['gap', 'sand', 'lob'].reduce(
    (acc: number, t: string) => acc + (data.wedge_types?.[t]?.length ?? 0),
    0
  );

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>

      {/* ── Section 1: Header ── */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Wedge Recommendations
        </h1>

        {/* Scoring mode badge row */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            data.metadata?.scoring_mode === 'rule_based'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-green/15 text-green-ink'
          }`}>
            {data.metadata?.scoring_mode === 'rule_based' ? 'Physics + AI' : 'ML Model'}
          </span>
          {data.metadata?.scoring_version && (
            <span className="text-xs text-gray-500">
              {data.metadata.scoring_version}
            </span>
          )}
          {data.metadata?.total_wedges_evaluated > 0 && (
            <span className="text-xs text-gray-400">
              ({data.metadata.total_wedges_evaluated.toLocaleString()} wedges
              evaluated)
            </span>
          )}
        </div>

        {/* Ideal loft chips */}
        {idealLofts && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-xs text-gray-500 font-medium">Targeting:</span>
            {idealLofts.gap !== undefined && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green/10 text-green-ink border border-green-ink">
                {idealLofts.gap}° gap
              </span>
            )}
            {idealLofts.sand !== undefined && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green/10 text-green-ink border border-green-ink">
                {idealLofts.sand}° sand
              </span>
            )}
            {idealLofts.lob !== undefined && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green/10 text-green-ink border border-green-ink">
                {idealLofts.lob}° lob
              </span>
            )}
          </div>
        )}

        {/* Existing loft preservation banner */}
        {hasExistingLofts && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg
                className="h-4 w-4 text-blue-500 mt-0.5 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-blue-800">
                Your existing wedge lofts are being preserved &mdash; lofts
                matching your current set (
                {existingLofts.map((l: number) => `${l}°`).join(', ')}) score
                10/10 for loft fit.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 2: Shaft Recommendation ── */}
      {shaft && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-1">
            Wedge Shaft Recommendation
          </h3>
          <p className="text-blue-800 font-medium">{shaft.shaft_name}</p>
          <p className="text-blue-700 text-sm">
            {[shaft.shaft_flex, shaft.material, shaft.weight_approx]
              .filter(Boolean)
              .join(' • ')}
          </p>
          <p className="text-blue-700 text-sm mt-1">{shaft.reason}</p>

          {shaft.note && (
            <div className="mt-2">
              <button
                onClick={() => setShaftNoteCollapsed(prev => !prev)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                About wedge shafts
                <svg
                  className={`w-3 h-3 transition-transform ${
                    shaftNoteCollapsed ? '' : 'rotate-180'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {!shaftNoteCollapsed && (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {shaft.note}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Section 3: Per-category wedge lists ── */}
      {(['gap', 'sand', 'lob'] as const).map(wtype => {
        const wedges: WedgeItem[] = data.wedge_types?.[wtype] ?? [];
        if (wedges.length === 0) return null;

        const idealLoftForType = idealLofts?.[wtype];
        const isPreserving =
          idealLoftForType !== undefined && matchesExistingLoft(idealLoftForType);

        return (
          <div key={wtype} className="mb-8">
            {/* Category header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-gray-900">
                {CATEGORY_LABELS[wtype]}
              </h2>
              {idealLoftForType !== undefined && (
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    isPreserving
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-green/15 text-green-ink'
                  }`}
                >
                  {isPreserving
                    ? `Preserving ${idealLoftForType}°`
                    : `Targeting ${idealLoftForType}°`}
                </span>
              )}
            </div>

            {/* Wedge cards */}
            <div className="space-y-3">
              {wedges.map((wedge, i) => renderWedgeCard(wedge, i + 1))}
            </div>
          </div>
        );
      })}

      {/* ── Footer ── */}
      <div className="mt-4 text-center text-sm text-gray-500">
        Showing top {totalShown} wedges across {['gap', 'sand', 'lob'].filter(t => (data.wedge_types?.[t]?.length ?? 0) > 0).length} categories.
        Recommendations are based on your swing profile.
      </div>
    </div>
  );
};

export default WedgeRecommendationResults;
